/**
 * 統合エラーハンドリングシステム
 * Progressive Loading、Web Workers、Performance Monitor の
 * エラーを統一的に処理・回復するシステム
 */

// ========================================
// Type Definitions
// ========================================

/**
 * エラーの重要度レベル
 */
export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';
import { logger } from '@/utils/logger.js';

/**
 * エラータイプ
 */
export type ErrorType =
  | 'javascript'
  | 'promise'
  | 'resource'
  | 'progressive_loading'
  | 'web_worker'
  | 'performance_monitor'
  | 'service_worker'
  | 'recovery_failure'
  | string;

/**
 * エラーコンテキスト情報
 */
export interface ErrorContext {
  component?: string;
  action?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * エラー情報
 */
export interface ErrorInfo {
  type: ErrorType;
  message: string;
  code?: string | number;
  severity: ErrorSeverity;
  stack?: string;
  timestamp: number;
  context?: ErrorContext;

  // JavaScript error specific
  filename?: string;
  lineno?: number;
  colno?: number;

  // Promise error specific
  promise?: Promise<unknown>;

  // Resource error specific
  element?: string;
  src?: string | undefined;

  // Recovery error specific
  originalError?: ErrorInfo;

  // Additional metadata
  [key: string]: unknown;
}

/**
 * エラーハンドラーコールバック型
 */
export type ErrorHandlerCallback = (errorInfo: ErrorInfo) => void;

/**
 * エラーフィルター関数型
 */
export type ErrorFilter = (errorInfo: ErrorInfo) => boolean;

/**
 * ハンドラーオプション
 */
export interface HandlerOptions {
  enableLogging?: boolean;
  maxErrors?: number;
  reportToServer?: boolean;
  filters?: ErrorFilter[];
}

/**
 * エラー統計情報
 */
export interface ErrorStats {
  totalErrors: number;
  recoveredErrors: number;
  criticalErrors: number;
  errorsByType: Map<ErrorType, number>;
  recoveryRate?: number;
  recentErrors?: ErrorInfo[];
}

/**
 * エラー統計情報（外部公開用）
 */
export interface PublicErrorStats {
  totalErrors: number;
  recoveredErrors: number;
  criticalErrors: number;
  errorsByType: Record<string, number>;
  recoveryRate: number;
  recentErrors: ErrorInfo[];
}

/**
 * ヘルスチェック関数型
 */
type HealthCheckFunction = () => void;

/**
 * 通知タイプ
 */
type NotificationType = 'error' | 'warning' | 'info';

// ========================================
// Window Interface Extension
// ========================================
// Window types are defined in global.d.ts

// ========================================
// ErrorHandler Class
// ========================================

class ErrorHandler {
  private errorQueue: ErrorInfo[];
  private errorListeners: Map<ErrorType, ErrorHandlerCallback[]>;
  private retryAttempts: Map<string, number>;
  private maxRetries: number;
  private suppressedErrors: Set<string>;
  private stats: ErrorStats;

  constructor() {
    this.errorQueue = [];
    this.errorListeners = new Map<ErrorType, ErrorHandlerCallback[]>();
    this.retryAttempts = new Map<string, number>();
    this.maxRetries = 3;
    this.suppressedErrors = new Set<string>();

    // エラー統計
    this.stats = {
      totalErrors: 0,
      recoveredErrors: 0,
      criticalErrors: 0,
      errorsByType: new Map<ErrorType, number>()
    };

    this.setupGlobalErrorHandling();
    this.initializeErrorRecovery();
  }

  /**
   * グローバルエラーハンドリング設定
   */
  private setupGlobalErrorHandling(): void {
    // JavaScript エラー
    window.addEventListener('error', (event: ErrorEvent): void => {
      this.handleError({
        type: 'javascript',
        message: event.error?.message || event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: Date.now(),
        severity: 'error'
      });
    });

    // Promise rejection エラー
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
      this.handleError({
        type: 'promise',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        promise: event.promise,
        timestamp: Date.now(),
        severity: 'error'
      });
    });

    // Resource loading エラー
    window.addEventListener('error', (event: Event): void => {
      const target = event.target;
      if (target && target !== window && target instanceof HTMLElement) {
        const element = target as HTMLElement & { src?: string; href?: string; tagName?: string };
        this.handleError({
          type: 'resource',
          message: `Failed to load resource: ${element.src || element.href || 'unknown'}`,
          element: element.tagName,
          src: element.src || element.href,
          timestamp: Date.now(),
          severity: 'warning'
        });
      }
    }, true);
  }

  /**
   * エラー処理メイン関数
   */
  public handleError(errorInfo: ErrorInfo): void {
    // 重複エラーの抑制
    const errorKey = `${errorInfo.type}_${errorInfo.message}`;
    if (this.suppressedErrors.has(errorKey)) {
      return;
    }

    // エラー統計更新
    this.updateErrorStats(errorInfo);

    // エラーキューに追加
    this.errorQueue.push(errorInfo);

    // 重要度に応じた処理
    switch (errorInfo.severity) {
      case 'critical':
        this.handleCriticalError(errorInfo);
        break;
      case 'error':
        this.handleRecoverableError(errorInfo);
        break;
      case 'warning':
        this.handleWarning(errorInfo);
        break;
      default:
        this.handleGenericError(errorInfo);
    }

    // エラーリスナーに通知
    this.notifyErrorListeners(errorInfo);

    // ログ出力
    this.logError(errorInfo);
  }

  /**
   * 重要なエラーの処理
   */
  private handleCriticalError(errorInfo: ErrorInfo): void {
    logger.error('🚨 Critical Error:', errorInfo);

    // ユーザーに通知
    this.showErrorNotification('重大なエラーが発生しました', errorInfo.message, 'error');

    // システム状態を安全モードに変更
    this.enterSafeMode();

    // エラーレポート送信（実装時）
    this.sendErrorReport(errorInfo);
  }

  /**
   * 回復可能なエラーの処理
   */
  private handleRecoverableError(errorInfo: ErrorInfo): void {
    logger.warn('⚠️ Recoverable Error:', errorInfo);

    const retryKey = `${errorInfo.type}_${errorInfo.message}`;
    const currentRetries = this.retryAttempts.get(retryKey) || 0;

    if (currentRetries < this.maxRetries) {
      this.retryAttempts.set(retryKey, currentRetries + 1);

      // 自動回復を試行
      setTimeout((): void => {
        this.attemptErrorRecovery(errorInfo);
      }, Math.pow(2, currentRetries) * 1000); // 指数バックオフ
    } else {
      // 最大試行回数に達した場合
      this.showErrorNotification('エラーが継続しています', '自動回復に失敗しました', 'warning');
      this.suppressedErrors.add(retryKey);
    }
  }

  /**
   * 警告レベルのエラー処理
   */
  private handleWarning(errorInfo: ErrorInfo): void {
    logger.warn('⚠️ Warning:', errorInfo);

    // リソース読み込みエラーの場合はフォールバック処理
    if (errorInfo.type === 'resource') {
      this.handleResourceError(errorInfo);
    }
  }

  /**
   * 一般的なエラー処理
   */
  private handleGenericError(_errorInfo: ErrorInfo): void {
    // ログ記録のみ
  }

  /**
   * リソースエラーの処理
   */
  private handleResourceError(errorInfo: ErrorInfo): void {
    if (errorInfo.src) {
      // 画像の場合はフォールバック画像を設定
      if (errorInfo.element === 'IMG') {
        this.loadFallbackImage(errorInfo.src);
      }
      // スクリプトの場合は代替読み込みを試行
      else if (errorInfo.element === 'SCRIPT') {
        this.loadFallbackScript(errorInfo.src);
      }
    }
  }

  /**
   * フォールバック画像読み込み
   */
  private loadFallbackImage(originalSrc: string): void {
    const images = document.querySelectorAll<HTMLImageElement>(`img[src="${originalSrc}"]`);
    images.forEach((img: HTMLImageElement): void => {
      img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPgogIDx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZvbnQtZmFtaWx5PSJzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNjY2Ij7nlLvlg4HjgojjgovjgarjgZw8L3RleHQ+Cjwvc3ZnPg==';
      img.alt = '画像を読み込めませんでした';
    });
  }

  /**
   * フォールバックスクリプト読み込み
   */
  private loadFallbackScript(originalSrc: string): void {
    logger.warn(`Script loading failed: ${originalSrc}`);

    // CDN の場合は別の CDN を試行
    if (originalSrc.includes('cdnjs.cloudflare.com')) {
      const fallbackSrc = originalSrc.replace('cdnjs.cloudflare.com', 'cdn.jsdelivr.net');
      this.loadScript(fallbackSrc).catch((error: Error): void => {
        logger.error('Fallback script loading also failed:', error);
      });
    }
  }

  /**
   * スクリプト動的読み込み
   */
  private loadScript(src: string): Promise<void> {
    return new Promise<void>((resolve, reject): void => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = (): void => resolve();
      script.onerror = (): void => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * エラー回復の試行
   */
  private async attemptErrorRecovery(errorInfo: ErrorInfo): Promise<void> {
    try {
      switch (errorInfo.type) {
        case 'progressive_loading':
          await this.recoverProgressiveLoading();
          break;
        case 'web_worker':
          await this.recoverWebWorker();
          break;
        case 'performance_monitor':
          await this.recoverPerformanceMonitor();
          break;
        case 'service_worker':
          await this.recoverServiceWorker();
          break;
        default:
          logger.warn('No specific recovery method for:', errorInfo.type);
      }

      this.stats.recoveredErrors++;
      logger.info('✅ Error recovery successful for:', errorInfo.type);

    } catch (recoveryError) {
      const error = recoveryError as Error;
      logger.error('❌ Error recovery failed:', recoveryError);
      this.handleError({
        type: 'recovery_failure',
        message: `Recovery failed for ${errorInfo.type}: ${error.message}`,
        originalError: errorInfo,
        timestamp: Date.now(),
        severity: 'error'
      });
    }
  }

  /**
   * Progressive Loading 回復
   */
  private async recoverProgressiveLoading(): Promise<void> {
    if (window.ProgressiveLoader) {
      const loader = new window.ProgressiveLoader();
      await loader.loadFallbackResource('essential-textures');
    }
  }

  /**
   * Web Worker 回復
   */
  private async recoverWebWorker(): Promise<void> {
    if (window.RenderingWorker && window.renderingWorker) {
      // 現在のワーカーを終了
      window.renderingWorker.terminate();

      // 新しいワーカーを作成
      window.renderingWorker = new window.RenderingWorker();
    }
  }

  /**
   * Performance Monitor 回復
   */
  private async recoverPerformanceMonitor(): Promise<void> {
    if (window.PerformanceMonitor) {
      // 既存のモニターを停止
      if (window.performanceMonitor && typeof window.performanceMonitor.destroy === 'function') {
        window.performanceMonitor.destroy();
      }

      // 新しいモニターを作成
      window.performanceMonitor = new window.PerformanceMonitor({ autoStart: true }) as typeof window.performanceMonitor;
    }
  }

  /**
   * Service Worker 回復
   */
  private async recoverServiceWorker(): Promise<ServiceWorkerRegistration> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none'
        });
        return registration;
      } catch (error) {
        const err = error as Error;
        throw new Error(`Service Worker recovery failed: ${err.message}`);
      }
    }
    throw new Error('Service Worker not supported');
  }

  /**
   * 安全モードの開始
   */
  private enterSafeMode(): void {
    logger.warn('🛡️ Entering Safe Mode');

    // パフォーマンス系機能を無効化
    if (window.performanceMonitor && typeof window.performanceMonitor.hide === 'function') {
      window.performanceMonitor.hide();
    }

    if (window.renderingWorker) {
      window.renderingWorker.terminate();
    }

    // 最小限の機能のみ有効化
    document.body.classList.add('safe-mode');

    this.showErrorNotification(
      '安全モード',
      'システムを安全モードで実行しています',
      'info'
    );
  }

  /**
   * エラー通知表示
   */
  private showErrorNotification(title: string, message: string, type: NotificationType = 'error'): void {
    const notification = document.createElement('div');
    notification.className = `error-notification error-${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#ff4444' : type === 'warning' ? '#ff8800' : '#4299e1'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10002;
      max-width: 350px;
      animation: slideInRight 0.3s ease;
    `;

    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 20px;">
          ${type === 'error' ? '🚨' : type === 'warning' ? '⚠️' : 'ℹ️'}
        </span>
        <div>
          <strong>${title}</strong><br>
          <small>${message}</small>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          margin-left: auto;
        ">×</button>
      </div>
    `;

    document.body.appendChild(notification);

    // 自動で消去（エラーは長めに表示）
    setTimeout((): void => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, type === 'error' ? 10000 : 5000);
  }

  /**
   * エラー統計更新
   */
  private updateErrorStats(errorInfo: ErrorInfo): void {
    this.stats.totalErrors++;

    if (errorInfo.severity === 'critical') {
      this.stats.criticalErrors++;
    }

    const errorType = errorInfo.type;
    this.stats.errorsByType.set(
      errorType,
      (this.stats.errorsByType.get(errorType) || 0) + 1
    );
  }

  /**
   * エラーリスナー通知
   */
  private notifyErrorListeners(errorInfo: ErrorInfo): void {
    const listeners = this.errorListeners.get(errorInfo.type) || [];
    listeners.forEach((listener: ErrorHandlerCallback): void => {
      try {
        listener(errorInfo);
      } catch (error) {
        logger.error('Error in error listener:', error);
      }
    });
  }

  /**
   * エラーログ出力
   */
  private logError(errorInfo: ErrorInfo): void {
    const logLevel = errorInfo.severity === 'critical' ? 'error' :
                    errorInfo.severity === 'error' ? 'error' : 'warn';

    console[logLevel](`[ErrorHandler] ${errorInfo.type}:`, {
      ...errorInfo,
      timestamp: new Date(errorInfo.timestamp).toISOString()
    });
  }

  /**
   * エラーレポート送信（将来の実装用）
   */
  private sendErrorReport(_errorInfo: ErrorInfo): void {
    // 実装時: サーバーにエラー情報を送信
  }

  /**
   * エラー回復システム初期化
   */
  private initializeErrorRecovery(): void {
    // 定期的なヘルスチェック
    setInterval((): void => {
      this.performHealthCheck();
    }, 30000); // 30秒ごと
  }

  /**
   * ヘルスチェック実行
   */
  private performHealthCheck(): void {
    const checks: HealthCheckFunction[] = [
      this.checkProgressiveLoader,
      this.checkWebWorkers,
      this.checkPerformanceMonitor,
      this.checkServiceWorker
    ];

    checks.forEach((check: HealthCheckFunction): void => {
      try {
        check.call(this);
      } catch (error) {
        logger.warn('Health check failed:', error);
      }
    });
  }

  /**
   * システム健全性チェック
   */
  private checkProgressiveLoader(): void {
    if (window.progressiveLoader && typeof window.progressiveLoader.getLoadingStatus === 'function') {
      const status = window.progressiveLoader.getLoadingStatus();
      if (status.progress < 50) {
        logger.warn('Progressive Loader appears to be stalled');
      }
    }
  }

  private checkWebWorkers(): void {
    if (window.renderingWorker && typeof window.renderingWorker.getStatistics === 'function') {
      const stats = window.renderingWorker.getStatistics();
      if (stats.performance.errorRate > 0.1) {
        logger.warn('Web Worker error rate is high:', stats.performance.errorRate);
      }
    }
  }

  private checkPerformanceMonitor(): void {
    if (window.performanceMonitor && typeof window.performanceMonitor.getStatistics === 'function') {
      const stats = window.performanceMonitor.getStatistics();
      if (stats.current.fps < 15) {
        logger.warn('Low FPS detected:', stats.current.fps);
      }
    }
  }

  private checkServiceWorker(): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Service Worker が正常に動作しているかチェック
      navigator.serviceWorker.ready.then((registration: ServiceWorkerRegistration): void => {
        if (!registration.active) {
          logger.warn('Service Worker is not active');
        }
      }).catch((error: Error): void => {
        logger.error('Service Worker check failed:', error);
      });
    }
  }

  /**
   * エラーリスナー追加
   */
  public addEventListener(errorType: ErrorType, listener: ErrorHandlerCallback): void {
    if (!this.errorListeners.has(errorType)) {
      this.errorListeners.set(errorType, []);
    }
    this.errorListeners.get(errorType)!.push(listener);
  }

  /**
   * エラーリスナー削除
   */
  public removeEventListener(errorType: ErrorType, listener: ErrorHandlerCallback): void {
    const listeners = this.errorListeners.get(errorType);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * エラー統計取得
   */
  public getErrorStats(): PublicErrorStats {
    return {
      ...this.stats,
      errorsByType: Object.fromEntries(this.stats.errorsByType),
      recentErrors: this.errorQueue.slice(-10),
      recoveryRate: this.stats.totalErrors > 0 ?
        (this.stats.recoveredErrors / this.stats.totalErrors) * 100 : 0
    };
  }

  /**
   * エラーキュークリア
   */
  public clearErrorQueue(): void {
    this.errorQueue.length = 0;
    this.retryAttempts.clear();
    this.suppressedErrors.clear();
  }

  /**
   * 破棄処理
   */
  public destroy(): void {
    this.errorListeners.clear();
    this.clearErrorQueue();
  }
}

// ========================================
// CSS Animation & Styles
// ========================================

const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  .safe-mode {
    filter: grayscale(50%);
  }

  .safe-mode::before {
    content: '🛡️ Safe Mode';
    position: fixed;
    bottom: 20px;
    left: 20px;
    background: rgba(255, 136, 0, 0.9);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    z-index: 1000;
  }
`;
document.head.appendChild(style);

// ========================================
// Module Export
// ========================================

// グローバルエラーハンドラーのインスタンス作成
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', (): void => {
    window.globalErrorHandler = new ErrorHandler();
  });
}

// ========================================
// Default Export
// ========================================

export default ErrorHandler;
